import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { type ReactNode, useRef } from "react";
import { acceptFor, UPLOAD_LIMITS } from "@/constants/uploadLimits";
import type { MediaType } from "@/interfaces/uploads";

interface UploadCardProps {
  previewUrl: string;
  onFile: (file: File) => void;
  /** Drives the accepted formats + size cap from the central UPLOAD_LIMITS. */
  mediaType: MediaType;
  emptyLabel: string;
  emptyHint?: string;
  isUploading?: boolean;
  children: ReactNode;
}

export function UploadCard({
  previewUrl,
  onFile,
  mediaType,
  emptyLabel,
  emptyHint,
  isUploading = false,
  children,
}: UploadCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const limit = UPLOAD_LIMITS[mediaType];

  return (
    <>
      {previewUrl ? (
        children
      ) : (
        <button
          type="button"
          onClick={() => !isUploading && inputRef.current?.click()}
          disabled={isUploading}
          className="w-full flex flex-col items-center gap-2 py-5 rounded-sm transition-colors hover:bg-light-dust disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ border: "2px dashed var(--color-smoke)" }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: "var(--color-graphite)" }} />
          <span className="font-lato text-[13px] font-semibold text-graphite">
            {isUploading ? "Uploading…" : emptyLabel}
          </span>
          {emptyHint && !isUploading && (
            <span className="font-lato text-[11px] text-graphite opacity-70">{emptyHint}</span>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={acceptFor(mediaType)}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            if (!limit.mimeTypes.includes(file.type)) {
              alert(`Invalid file type. Please upload one of: ${limit.mimeTypes.join(", ")}.`);
            } else if (file.size > limit.maxBytes) {
              alert(`File is too large. Please upload an image under ${limit.maxSizeLabel}.`);
            } else {
              onFile(file);
            }
          }
          e.target.value = "";
        }}
      />
    </>
  );
}
